// Backend integration point: Replace mock data with API calls to /api/schemes

export type SchemeCategory =
  | 'Education'
  | 'Agriculture'
  | 'Health'
  | 'Business'
  | 'Women'
  | 'Housing'
  | 'Social Welfare'
  | 'Rural Development'
  | 'Skill Development';

export type SchemeStatus = 'Active' | 'Upcoming' | 'Expired' | 'Ongoing';

export interface SchemeBenefit {
  id: string;
  label: string;
}

export interface SchemeEligibility {
  id: string;
  criterion: string;
}

export interface SchemeDocument {
  id: string;
  name: string;
  mandatory: boolean;
  format?: string;
}

export interface ApplicationStep {
  id: string;
  stepNumber: number;
  title: string;
  description: string;
  portal?: string;
  portalUrl?: string;
}

export interface Scheme {
  id: string;
  title: string;
  shortTitle: string;
  ministry: string;
  category: SchemeCategory;
  status: SchemeStatus;
  description: string;
  shortDescription: string;
  benefits: SchemeBenefit[];
  eligibility: SchemeEligibility[];
  documents: SchemeDocument[];
  applicationSteps: ApplicationStep[];
  launchDate: string;
  deadline: string | null;
  beneficiaryCount: string;
  fundingAmount: string;
  states: string[];
  schemeCode: string;
  officialUrl: string;
  isNew: boolean;
  isFeatured: boolean;
  tags: string[];
  minAge?: number;
  maxAge?: number;
  maxIncome?: number;
  gender?: 'All' | 'Female' | 'Male';
}

export const SCHEMES: Scheme[] = [
  {
    id: 'scheme-001',
    title: 'Pradhan Mantri Kisan Samman Nidhi (PM-KISAN)',
    shortTitle: 'PM-KISAN',
    ministry: 'Ministry of Agriculture & Farmers Welfare',
    category: 'Agriculture',
    status: 'Active',
    description:
      'PM-KISAN provides income support of ₹6,000 per year to all landholding farmer families across India in three equal installments of ₹2,000 every four months. The scheme aims to supplement the financial needs of farmers in procuring various inputs to ensure proper crop health and appropriate yields, commensurate with the anticipated farm income.',
    shortDescription:
      'Income support of ₹6,000/year to small and marginal farmer families in three installments.',
    benefits: [
      { id: 'b-001-1', label: '₹6,000 annual financial assistance' },
      { id: 'b-001-2', label: 'Direct benefit transfer to bank account' },
      { id: 'b-001-3', label: 'No intermediaries — 100% DBT' },
      { id: 'b-001-4', label: 'Covers all landholding farmer families' },
    ],
    eligibility: [
      { id: 'e-001-1', criterion: 'Must be a landholding farmer family' },
      { id: 'e-001-2', criterion: 'Land holding data verified by state/UT government' },
      { id: 'e-001-3', criterion: 'Not holding constitutional post or government job' },
      { id: 'e-001-4', criterion: 'Not a taxpayer in the last assessment year' },
      { id: 'e-001-5', criterion: 'Not a retired pensioner with ₹10,000+/month pension' },
    ],
    documents: [
      { id: 'd-001-1', name: 'Aadhaar Card', mandatory: true, format: 'Original + Photocopy' },
      { id: 'd-001-2', name: 'Land Records / Khatoni', mandatory: true, format: 'Original' },
      {
        id: 'd-001-3',
        name: 'Bank Passbook / Account Details',
        mandatory: true,
        format: 'Photocopy',
      },
      { id: 'd-001-4', name: 'Mobile Number linked to Aadhaar', mandatory: true },
    ],
    applicationSteps: [
      {
        id: 'as-001-1',
        stepNumber: 1,
        title: 'Visit Official Portal',
        description: 'Go to pmkisan.gov.in and click on "New Farmer Registration"',
        portal: 'pmkisan.gov.in',
        portalUrl: 'https://pmkisan.gov.in',
      },
      {
        id: 'as-001-2',
        stepNumber: 2,
        title: 'Enter Aadhaar Details',
        description: 'Enter your 12-digit Aadhaar number and complete OTP verification',
      },
      {
        id: 'as-001-3',
        stepNumber: 3,
        title: 'Fill Registration Form',
        description: 'Enter personal details, land records, and bank account information',
      },
      {
        id: 'as-001-4',
        stepNumber: 4,
        title: 'Submit & Get Reference Number',
        description: 'Submit the form and note down your registration reference number',
      },
      {
        id: 'as-001-5',
        stepNumber: 5,
        title: 'State Verification',
        description: 'State government verifies land records. Approval takes 3–4 weeks.',
      },
    ],
    launchDate: '01/12/2018',
    deadline: null,
    beneficiaryCount: '11.8 Crore',
    fundingAmount: '₹6,000/year',
    states: ['All States'],
    schemeCode: 'PMKISAN-2018',
    officialUrl: 'https://pmkisan.gov.in',
    isNew: false,
    isFeatured: true,
    tags: ['farmer', 'income support', 'DBT', 'agriculture'],
    minAge: 18,
    maxAge: undefined,
    maxIncome: undefined,
    gender: 'All',
  },
  {
    id: 'scheme-002',
    title: 'Pradhan Mantri Awas Yojana — Urban (PMAY-U)',
    shortTitle: 'PMAY Urban',
    ministry: 'Ministry of Housing & Urban Affairs',
    category: 'Housing',
    status: 'Active',
    description:
      'PMAY-U provides affordable housing to urban poor including slum dwellers. The scheme offers credit-linked subsidy, in-situ slum redevelopment, affordable housing in partnership with private sector, and beneficiary-led construction for eligible urban households.',
    shortDescription:
      'Affordable housing assistance and credit-linked subsidy for eligible urban households.',
    benefits: [
      { id: 'b-002-1', label: 'Credit-linked subsidy up to ₹2.67 lakh' },
      { id: 'b-002-2', label: 'Pucca house with basic amenities' },
      { id: 'b-002-3', label: 'Interest subsidy on home loans' },
      { id: 'b-002-4', label: 'In-situ slum redevelopment support' },
    ],
    eligibility: [
      { id: 'e-002-1', criterion: 'Must be an urban resident without pucca house' },
      { id: 'e-002-2', criterion: 'EWS: Annual income up to ₹3 lakh' },
      { id: 'e-002-3', criterion: 'LIG: Annual income ₹3–6 lakh' },
      { id: 'e-002-4', criterion: 'MIG-I: Annual income ₹6–12 lakh' },
      { id: 'e-002-5', criterion: "House must be registered in woman's name (EWS/LIG)" },
    ],
    documents: [
      { id: 'd-002-1', name: 'Aadhaar Card', mandatory: true },
      { id: 'd-002-2', name: 'Income Certificate', mandatory: true },
      { id: 'd-002-3', name: 'Bank Statement (6 months)', mandatory: true },
      { id: 'd-002-4', name: 'Property Documents / Affidavit of No Property', mandatory: true },
      { id: 'd-002-5', name: 'Caste Certificate (if SC/ST/OBC)', mandatory: false },
    ],
    applicationSteps: [
      {
        id: 'as-002-1',
        stepNumber: 1,
        title: 'Visit PMAY Portal',
        description: 'Go to pmaymis.gov.in and select your category',
        portal: 'pmaymis.gov.in',
        portalUrl: 'https://pmaymis.gov.in',
      },
      {
        id: 'as-002-2',
        stepNumber: 2,
        title: 'Check Beneficiary List',
        description: 'Verify if your name appears in the beneficiary list',
      },
      {
        id: 'as-002-3',
        stepNumber: 3,
        title: 'Apply Through Bank',
        description: 'For CLSS, apply through a Primary Lending Institution (PLI) bank',
      },
      {
        id: 'as-002-4',
        stepNumber: 4,
        title: 'Submit Documents',
        description: 'Submit income proof, identity documents, and property papers to the bank',
      },
      {
        id: 'as-002-5',
        stepNumber: 5,
        title: 'Loan Approval & Subsidy',
        description:
          'Bank processes the application and subsidy is credited directly to loan account',
      },
    ],
    launchDate: '25/06/2015',
    deadline: '31/12/2026',
    beneficiaryCount: '1.18 Crore',
    fundingAmount: 'Up to ₹2.67 lakh subsidy',
    states: ['All Urban Areas'],
    schemeCode: 'PMAY-U-2015',
    officialUrl: 'https://pmaymis.gov.in',
    isNew: false,
    isFeatured: true,
    tags: ['housing', 'urban', 'subsidy', 'home loan'],
    minAge: 18,
    maxAge: undefined,
    maxIncome: 1200000,
    gender: 'All',
  },
  {
    id: 'scheme-003',
    title: 'National Scholarship Portal — Central Schemes',
    shortTitle: 'NSP Scholarship',
    ministry: 'Ministry of Education',
    category: 'Education',
    status: 'Active',
    description:
      'The National Scholarship Portal offers a one-stop platform for multiple scholarship schemes for students from Class 1 to PhD level. It covers pre-matric, post-matric, and merit-cum-means scholarships for SC, ST, OBC, Minority communities, and economically weaker sections.',
    shortDescription:
      'One-stop portal for pre-matric and post-matric scholarships for SC/ST/OBC/Minority students.',
    benefits: [
      { id: 'b-003-1', label: 'Scholarship amount from ₹1,000 to ₹20,000/year' },
      { id: 'b-003-2', label: 'Covers tuition fees, maintenance allowance' },
      { id: 'b-003-3', label: 'Applicable from Class 1 to PhD level' },
      { id: 'b-003-4', label: 'Merit-cum-means scholarships for professional courses' },
    ],
    eligibility: [
      { id: 'e-003-1', criterion: 'Must be Indian national enrolled in a recognized institution' },
      { id: 'e-003-2', criterion: 'SC/ST/OBC/Minority/Economically Weaker Section' },
      { id: 'e-003-3', criterion: 'Family income below ₹2.5 lakh per annum (most schemes)' },
      { id: 'e-003-4', criterion: 'Minimum 50% marks in previous examination' },
      { id: 'e-003-5', criterion: 'Not availing benefit from any other scholarship' },
    ],
    documents: [
      { id: 'd-003-1', name: 'Aadhaar Card', mandatory: true },
      { id: 'd-003-2', name: 'Bank Account linked to Aadhaar', mandatory: true },
      { id: 'd-003-3', name: 'Income Certificate', mandatory: true },
      { id: 'd-003-4', name: 'Caste / Community Certificate', mandatory: true },
      { id: 'd-003-5', name: 'Previous Year Marksheet', mandatory: true },
      { id: 'd-003-6', name: 'Bonafide Certificate from Institution', mandatory: true },
    ],
    applicationSteps: [
      {
        id: 'as-003-1',
        stepNumber: 1,
        title: 'Register on NSP',
        description: 'Visit scholarships.gov.in and register with Aadhaar-linked mobile number',
        portal: 'scholarships.gov.in',
        portalUrl: 'https://scholarships.gov.in',
      },
      {
        id: 'as-003-2',
        stepNumber: 2,
        title: 'Select Scholarship',
        description: 'Browse and select the appropriate scholarship scheme for your category',
      },
      {
        id: 'as-003-3',
        stepNumber: 3,
        title: 'Fill Application Form',
        description: 'Complete the application form with academic and personal details',
      },
      {
        id: 'as-003-4',
        stepNumber: 4,
        title: 'Upload Documents',
        description: 'Upload scanned copies of all required documents',
      },
      {
        id: 'as-003-5',
        stepNumber: 5,
        title: 'Institute Verification',
        description: 'Your institution verifies the application before it reaches the ministry',
      },
    ],
    launchDate: '01/07/2015',
    deadline: '31/10/2026',
    beneficiaryCount: '1.25 Crore',
    fundingAmount: '₹1,000–₹20,000/year',
    states: ['All States'],
    schemeCode: 'NSP-EDU-2015',
    officialUrl: 'https://scholarships.gov.in',
    isNew: false,
    isFeatured: true,
    tags: ['scholarship', 'education', 'SC/ST', 'OBC', 'minority', 'student'],
    minAge: 5,
    maxAge: 35,
    maxIncome: 250000,
    gender: 'All',
  },
  {
    id: 'scheme-004',
    title: 'PM Vishwakarma Yojana',
    shortTitle: 'PM Vishwakarma',
    ministry: 'Ministry of Micro, Small & Medium Enterprises',
    category: 'Skill Development',
    status: 'Active',
    description:
      'PM Vishwakarma Yojana is a Central Sector Scheme to provide end-to-end support to artisans and craftspeople who work with their hands and tools across 18 traditional trades. The scheme provides recognition, skill upgradation, access to modern tools, and credit support.',
    shortDescription:
      'Support for artisans and craftspeople in 18 trades — skills, tools, and collateral-free credit.',
    benefits: [
      { id: 'b-004-1', label: 'Collateral-free credit up to ₹3 lakh' },
      { id: 'b-004-2', label: 'Skill training with ₹500/day stipend' },
      { id: 'b-004-3', label: 'Modern tool kit support up to ₹15,000' },
      { id: 'b-004-4', label: 'PM Vishwakarma certificate and ID card' },
      { id: 'b-004-5', label: 'Digital transaction incentive ₹1/transaction (up to 100)' },
    ],
    eligibility: [
      { id: 'e-004-1', criterion: 'Must be an artisan/craftsperson in one of 18 listed trades' },
      { id: 'e-004-2', criterion: 'Minimum 18 years of age' },
      { id: 'e-004-3', criterion: 'Not enrolled in PMEGP, PM SVANidhi, or Mudra in last 5 years' },
      { id: 'e-004-4', criterion: 'Only one member per family can avail the benefit' },
    ],
    documents: [
      { id: 'd-004-1', name: 'Aadhaar Card', mandatory: true },
      { id: 'd-004-2', name: 'Mobile Number linked to Aadhaar', mandatory: true },
      { id: 'd-004-3', name: 'Bank Account Details', mandatory: true },
      { id: 'd-004-4', name: 'Ration Card (for family verification)', mandatory: false },
    ],
    applicationSteps: [
      {
        id: 'as-004-1',
        stepNumber: 1,
        title: 'Visit CSC / Portal',
        description: 'Register at pmvishwakarma.gov.in or nearest Common Service Centre',
        portal: 'pmvishwakarma.gov.in',
        portalUrl: 'https://pmvishwakarma.gov.in',
      },
      {
        id: 'as-004-2',
        stepNumber: 2,
        title: 'Aadhaar Verification',
        description: 'Biometric verification at CSC using Aadhaar',
      },
      {
        id: 'as-004-3',
        stepNumber: 3,
        title: 'Trade Selection',
        description: 'Select your traditional trade from the 18 listed crafts',
      },
      {
        id: 'as-004-4',
        stepNumber: 4,
        title: 'Skill Training Registration',
        description: 'Enroll for basic 5-day skill training at nearest training centre',
      },
      {
        id: 'as-004-5',
        stepNumber: 5,
        title: 'Receive Certificate & Credit',
        description: 'Receive PM Vishwakarma certificate and apply for credit support',
      },
    ],
    launchDate: '17/09/2023',
    deadline: '31/03/2028',
    beneficiaryCount: '30 Lakh (Target)',
    fundingAmount: 'Up to ₹3 lakh credit',
    states: ['All States'],
    schemeCode: 'PMV-2023',
    officialUrl: 'https://pmvishwakarma.gov.in',
    isNew: true,
    isFeatured: true,
    tags: ['artisan', 'craftsperson', 'skill', 'MSME', 'credit', 'tools'],
    minAge: 18,
    maxAge: undefined,
    maxIncome: undefined,
    gender: 'All',
  },
  {
    id: 'scheme-005',
    title: 'Beti Bachao Beti Padhao Yojana',
    shortTitle: 'BBBP Yojana',
    ministry: 'Ministry of Women & Child Development',
    category: 'Women',
    status: 'Ongoing',
    description:
      'Beti Bachao Beti Padhao addresses the declining Child Sex Ratio (CSR) and empowers the girl child through education, health, and social welfare interventions. The scheme works through multi-sectoral interventions in districts with low CSR, ensuring girl children survive, thrive, and are educated.',
    shortDescription:
      'Scheme to address declining child sex ratio and ensure education and empowerment of the girl child.',
    benefits: [
      { id: 'b-005-1', label: 'Conditional cash transfer for girl child education' },
      { id: 'b-005-2', label: 'Free healthcare for girl child under PMSMA' },
      { id: 'b-005-3', label: 'Awareness campaigns in targeted districts' },
      { id: 'b-005-4', label: 'Sukanya Samriddhi Yojana account linkage' },
    ],
    eligibility: [
      { id: 'e-005-1', criterion: 'Girl child born on or after 2/12/2014' },
      { id: 'e-005-2', criterion: 'Resident of India' },
      { id: 'e-005-3', criterion: 'Applicable in 405 selected districts (Phase 1 & 2)' },
    ],
    documents: [
      { id: 'd-005-1', name: 'Birth Certificate of Girl Child', mandatory: true },
      { id: 'd-005-2', name: 'Aadhaar of Parents', mandatory: true },
      { id: 'd-005-3', name: 'Bank Account of Parents', mandatory: true },
    ],
    applicationSteps: [
      {
        id: 'as-005-1',
        stepNumber: 1,
        title: 'Contact Anganwadi / ASHA Worker',
        description: 'Register through your local Anganwadi centre or ASHA health worker',
      },
      {
        id: 'as-005-2',
        stepNumber: 2,
        title: 'Open Sukanya Samriddhi Account',
        description: "Open SSY account at post office or bank in girl child's name",
      },
      {
        id: 'as-005-3',
        stepNumber: 3,
        title: 'Register for Benefits',
        description: 'Submit documents at district WCD office for conditional cash transfers',
      },
    ],
    launchDate: '22/01/2015',
    deadline: null,
    beneficiaryCount: 'Ongoing — 8.5 Lakh+ beneficiaries',
    fundingAmount: 'Variable cash transfers',
    states: ['All States (405 districts)'],
    schemeCode: 'BBBP-2015',
    officialUrl: 'https://wcd.nic.in/bbbp-schemes',
    isNew: false,
    isFeatured: false,
    tags: ['girl child', 'women', 'education', 'health', 'empowerment'],
    minAge: 0,
    maxAge: 10,
    maxIncome: undefined,
    gender: 'Female',
  },
  {
    id: 'scheme-006',
    title: 'Ayushman Bharat — Pradhan Mantri Jan Arogya Yojana',
    shortTitle: 'AB-PMJAY',
    ministry: 'Ministry of Health & Family Welfare',
    category: 'Health',
    status: 'Active',
    description:
      'AB-PMJAY provides health cover of ₹5 lakh per family per year for secondary and tertiary care hospitalization to over 50 crore beneficiaries. It covers pre and post-hospitalization expenses and is cashless at empanelled public and private hospitals across India.',
    shortDescription:
      'Health cover of ₹5 lakh/year per family for hospitalization at empanelled hospitals — cashless.',
    benefits: [
      { id: 'b-006-1', label: '₹5 lakh health cover per family per year' },
      { id: 'b-006-2', label: 'Cashless treatment at 27,000+ hospitals' },
      { id: 'b-006-3', label: 'Pre and post-hospitalization expenses covered' },
      { id: 'b-006-4', label: '1,949 medical procedures covered' },
      { id: 'b-006-5', label: 'Portability — use across India' },
    ],
    eligibility: [
      { id: 'e-006-1', criterion: 'Families listed in SECC 2011 data (rural & urban poor)' },
      { id: 'e-006-2', criterion: 'Automatically enrolled — no registration needed' },
      { id: 'e-006-3', criterion: 'No cap on family size' },
      { id: 'e-006-4', criterion: 'Pre-existing diseases covered from day 1' },
    ],
    documents: [
      { id: 'd-006-1', name: 'Aadhaar Card / Ration Card', mandatory: true },
      { id: 'd-006-2', name: 'PMJAY e-Card (issued by scheme)', mandatory: false },
      { id: 'd-006-3', name: 'Mobile Number for OTP', mandatory: true },
    ],
    applicationSteps: [
      {
        id: 'as-006-1',
        stepNumber: 1,
        title: 'Check Eligibility',
        description: 'Visit pmjay.gov.in or call 14555 to check if your family is listed',
        portal: 'pmjay.gov.in',
        portalUrl: 'https://pmjay.gov.in',
      },
      {
        id: 'as-006-2',
        stepNumber: 2,
        title: 'Get Golden Card',
        description: 'Visit nearest empanelled hospital or Common Service Centre for Golden Card',
      },
      {
        id: 'as-006-3',
        stepNumber: 3,
        title: 'Cashless Hospitalization',
        description: 'Present Golden Card / Aadhaar at empanelled hospital for cashless treatment',
      },
    ],
    launchDate: '23/09/2018',
    deadline: null,
    beneficiaryCount: '55 Crore',
    fundingAmount: '₹5 lakh/family/year',
    states: ['All States (33 states/UTs)'],
    schemeCode: 'PMJAY-2018',
    officialUrl: 'https://pmjay.gov.in',
    isNew: false,
    isFeatured: true,
    tags: ['health', 'insurance', 'hospital', 'cashless', 'BPL'],
    minAge: 0,
    maxAge: undefined,
    maxIncome: undefined,
    gender: 'All',
  },
  {
    id: 'scheme-007',
    title: 'PM Mudra Yojana (PMMY)',
    shortTitle: 'PM Mudra',
    ministry: 'Ministry of Finance',
    category: 'Business',
    status: 'Active',
    description:
      'Pradhan Mantri Mudra Yojana provides collateral-free microfinance loans to non-corporate, non-farm small and micro enterprises. Loans are available under three categories — Shishu (up to ₹50,000), Kishore (₹50,001–₹5 lakh), and Tarun (₹5 lakh–₹10 lakh).',
    shortDescription:
      'Collateral-free business loans up to ₹10 lakh for small and micro enterprises under three tiers.',
    benefits: [
      { id: 'b-007-1', label: 'No collateral required' },
      { id: 'b-007-2', label: 'Shishu: Loans up to ₹50,000' },
      { id: 'b-007-3', label: 'Kishore: Loans ₹50,001–₹5 lakh' },
      { id: 'b-007-4', label: 'Tarun: Loans ₹5 lakh–₹10 lakh' },
      { id: 'b-007-5', label: 'Available through banks, MFIs, NBFCs' },
    ],
    eligibility: [
      { id: 'e-007-1', criterion: 'Non-farm micro and small enterprises' },
      { id: 'e-007-2', criterion: 'Individuals, proprietorships, partnership firms' },
      { id: 'e-007-3', criterion: 'Business must be in manufacturing, trading, or services' },
      { id: 'e-007-4', criterion: 'No default on previous loans' },
    ],
    documents: [
      { id: 'd-007-1', name: 'Aadhaar Card', mandatory: true },
      { id: 'd-007-2', name: 'PAN Card', mandatory: true },
      { id: 'd-007-3', name: 'Business Proof / Registration', mandatory: true },
      { id: 'd-007-4', name: 'Bank Statement (6 months)', mandatory: true },
      { id: 'd-007-5', name: 'Business Plan / Project Report', mandatory: false },
      { id: 'd-007-6', name: 'Quotation for Equipment (if applicable)', mandatory: false },
    ],
    applicationSteps: [
      {
        id: 'as-007-1',
        stepNumber: 1,
        title: 'Approach Lending Institution',
        description: 'Visit any bank, NBFC, or MFI offering MUDRA loans',
      },
      {
        id: 'as-007-2',
        stepNumber: 2,
        title: 'Fill MUDRA Loan Application',
        description: 'Complete the loan application form specifying the loan category',
      },
      {
        id: 'as-007-3',
        stepNumber: 3,
        title: 'Submit Documents',
        description: 'Submit business proof, identity documents, and bank statements',
      },
      {
        id: 'as-007-4',
        stepNumber: 4,
        title: 'Receive MUDRA Card',
        description: 'Upon approval, receive MUDRA debit card for working capital use',
      },
    ],
    launchDate: '08/04/2015',
    deadline: null,
    beneficiaryCount: '44 Crore (cumulative)',
    fundingAmount: 'Up to ₹10 lakh',
    states: ['All States'],
    schemeCode: 'PMMY-2015',
    officialUrl: 'https://mudra.org.in',
    isNew: false,
    isFeatured: true,
    tags: ['business', 'loan', 'MSME', 'microfinance', 'entrepreneur'],
    minAge: 18,
    maxAge: undefined,
    maxIncome: undefined,
    gender: 'All',
  },
  {
    id: 'scheme-008',
    title: 'Sukanya Samriddhi Yojana (SSY)',
    shortTitle: 'Sukanya Samriddhi',
    ministry: 'Ministry of Finance',
    category: 'Women',
    status: 'Active',
    description:
      'Sukanya Samriddhi Yojana is a small deposit scheme for the girl child as part of the Beti Bachao Beti Padhao campaign. The scheme offers the highest interest rate among government small savings schemes with tax exemption under Section 80C.',
    shortDescription:
      'High-interest savings scheme for girl child — tax-free returns, backed by Government of India.',
    benefits: [
      { id: 'b-008-1', label: '8.2% interest rate per annum (2024-25)' },
      { id: 'b-008-2', label: 'Tax exemption under Section 80C' },
      { id: 'b-008-3', label: "Account matures at 21 years of girl's age" },
      { id: 'b-008-4', label: 'Partial withdrawal for education at age 18' },
      { id: 'b-008-5', label: 'Minimum deposit ₹250/year, maximum ₹1.5 lakh/year' },
    ],
    eligibility: [
      { id: 'e-008-1', criterion: 'Account opened for girl child below 10 years of age' },
      { id: 'e-008-2', criterion: 'Maximum two accounts per family (for two girl children)' },
      { id: 'e-008-3', criterion: 'Account opened by parent or legal guardian' },
    ],
    documents: [
      { id: 'd-008-1', name: 'Birth Certificate of Girl Child', mandatory: true },
      { id: 'd-008-2', name: 'Aadhaar of Parent/Guardian', mandatory: true },
      { id: 'd-008-3', name: 'Address Proof', mandatory: true },
      { id: 'd-008-4', name: 'Photograph of Girl Child', mandatory: true },
    ],
    applicationSteps: [
      {
        id: 'as-008-1',
        stepNumber: 1,
        title: 'Visit Post Office or Bank',
        description: 'Go to any post office or authorized bank (SBI, nationalized banks)',
      },
      {
        id: 'as-008-2',
        stepNumber: 2,
        title: 'Fill Account Opening Form',
        description: "Fill the SSY account opening form with girl child's details",
      },
      {
        id: 'as-008-3',
        stepNumber: 3,
        title: 'Submit Documents & Initial Deposit',
        description: 'Submit documents and initial deposit of minimum ₹250',
      },
      {
        id: 'as-008-4',
        stepNumber: 4,
        title: 'Receive Passbook',
        description: 'Receive the SSY passbook for future deposits and tracking',
      },
    ],
    launchDate: '22/01/2015',
    deadline: null,
    beneficiaryCount: '3.2 Crore accounts',
    fundingAmount: '8.2% interest p.a.',
    states: ['All States'],
    schemeCode: 'SSY-2015',
    officialUrl: 'https://www.indiapost.gov.in',
    isNew: false,
    isFeatured: false,
    tags: ['girl child', 'savings', 'tax benefit', 'women', 'education'],
    minAge: 0,
    maxAge: 10,
    maxIncome: undefined,
    gender: 'Female',
  },
  {
    id: 'scheme-009',
    title: 'Pradhan Mantri Fasal Bima Yojana (PMFBY)',
    shortTitle: 'PMFBY',
    ministry: 'Ministry of Agriculture & Farmers Welfare',
    category: 'Agriculture',
    status: 'Active',
    description:
      'PMFBY provides comprehensive crop insurance coverage against crop failure due to non-preventable natural risks. Farmers pay a very low premium — 2% for Kharif, 1.5% for Rabi, and 5% for commercial/horticultural crops — with the balance paid by central and state governments.',
    shortDescription:
      'Crop insurance at 2% premium for Kharif and 1.5% for Rabi crops against natural calamities.',
    benefits: [
      { id: 'b-009-1', label: 'Insurance cover against crop failure' },
      { id: 'b-009-2', label: 'Only 2% premium for Kharif crops' },
      { id: 'b-009-3', label: 'Only 1.5% premium for Rabi crops' },
      { id: 'b-009-4', label: 'Covers drought, flood, hailstorm, landslide' },
      { id: 'b-009-5', label: 'Post-harvest losses also covered' },
    ],
    eligibility: [
      { id: 'e-009-1', criterion: 'All farmers including sharecroppers and tenant farmers' },
      { id: 'e-009-2', criterion: 'Mandatory for loanee farmers under KCC' },
      { id: 'e-009-3', criterion: 'Voluntary for non-loanee farmers' },
    ],
    documents: [
      { id: 'd-009-1', name: 'Aadhaar Card', mandatory: true },
      { id: 'd-009-2', name: 'Land Records / Khasra', mandatory: true },
      { id: 'd-009-3', name: 'Bank Account Details', mandatory: true },
      { id: 'd-009-4', name: 'Sowing Certificate', mandatory: true },
    ],
    applicationSteps: [
      {
        id: 'as-009-1',
        stepNumber: 1,
        title: 'Enroll Before Cut-off Date',
        description: 'Enroll before the last date for the respective crop season',
      },
      {
        id: 'as-009-2',
        stepNumber: 2,
        title: 'Apply via Bank or CSC',
        description: 'Apply at nearest bank branch or Common Service Centre',
      },
      {
        id: 'as-009-3',
        stepNumber: 3,
        title: 'Pay Premium',
        description: 'Pay the applicable premium amount (2% for Kharif, 1.5% for Rabi)',
      },
      {
        id: 'as-009-4',
        stepNumber: 4,
        title: 'Report Crop Damage',
        description: 'Report crop damage within 72 hours via app or call to 14447',
      },
    ],
    launchDate: '18/02/2016',
    deadline: '31/07/2026',
    beneficiaryCount: '5.6 Crore',
    fundingAmount: 'Full sum insured on crop loss',
    states: ['All States'],
    schemeCode: 'PMFBY-2016',
    officialUrl: 'https://pmfby.gov.in',
    isNew: false,
    isFeatured: false,
    tags: ['crop insurance', 'farmer', 'agriculture', 'natural calamity'],
    minAge: 18,
    maxAge: undefined,
    maxIncome: undefined,
    gender: 'All',
  },
  {
    id: 'scheme-010',
    title: 'Pradhan Mantri SVANidhi — Street Vendor Loan',
    shortTitle: 'PM SVANidhi',
    ministry: 'Ministry of Housing & Urban Affairs',
    category: 'Business',
    status: 'Active',
    description:
      'PM SVANidhi provides affordable working capital loans to street vendors to resume their livelihoods that were adversely affected due to COVID-19 lockdown. Vendors can avail initial loan of ₹10,000, enhanced to ₹20,000 and ₹50,000 upon timely repayment.',
    shortDescription:
      'Working capital loans for street vendors — start with ₹10,000, scale up to ₹50,000 with timely repayment.',
    benefits: [
      { id: 'b-010-1', label: 'Initial loan of ₹10,000 without collateral' },
      { id: 'b-010-2', label: 'Enhanced loans up to ₹50,000 on good repayment' },
      { id: 'b-010-3', label: '7% interest subsidy per annum' },
      { id: 'b-010-4', label: 'Digital transaction cashback up to ₹1,200/year' },
    ],
    eligibility: [
      { id: 'e-010-1', criterion: 'Street vendors vending in urban areas' },
      { id: 'e-010-2', criterion: 'Must hold Certificate of Vending or Letter of Recommendation' },
      { id: 'e-010-3', criterion: 'Vendors surveyed under PM SVANidhi survey' },
    ],
    documents: [
      { id: 'd-010-1', name: 'Aadhaar Card', mandatory: true },
      { id: 'd-010-2', name: 'Certificate of Vending / LOR', mandatory: true },
      { id: 'd-010-3', name: 'Bank Account Details', mandatory: true },
    ],
    applicationSteps: [
      {
        id: 'as-010-1',
        stepNumber: 1,
        title: 'Apply on Portal',
        description: 'Visit pmsvanidhi.mohua.gov.in or apply through ULB office',
        portal: 'pmsvanidhi.mohua.gov.in',
        portalUrl: 'https://pmsvanidhi.mohua.gov.in',
      },
      {
        id: 'as-010-2',
        stepNumber: 2,
        title: 'Get Certificate of Vending',
        description: 'Obtain Certificate of Vending from Urban Local Body (ULB)',
      },
      {
        id: 'as-010-3',
        stepNumber: 3,
        title: 'Loan Disbursement',
        description: 'Loan disbursed directly to bank account within 30 days',
      },
    ],
    launchDate: '01/06/2020',
    deadline: '31/12/2026',
    beneficiaryCount: '60 Lakh',
    fundingAmount: '₹10,000–₹50,000',
    states: ['All Urban Areas'],
    schemeCode: 'SVANIDHI-2020',
    officialUrl: 'https://pmsvanidhi.mohua.gov.in',
    isNew: false,
    isFeatured: false,
    tags: ['street vendor', 'loan', 'business', 'urban', 'working capital'],
    minAge: 18,
    maxAge: undefined,
    maxIncome: undefined,
    gender: 'All',
  },
  {
    id: 'scheme-011',
    title: 'Agri Infrastructure Fund (AIF)',
    shortTitle: 'Agri Infra Fund',
    ministry: 'Ministry of Agriculture & Farmers Welfare',
    category: 'Agriculture',
    status: 'Active',
    description:
      'The Agriculture Infrastructure Fund provides medium to long-term debt financing for investment in viable projects for post-harvest management infrastructure and community farming assets through interest subvention and financial support.',
    shortDescription:
      'Medium-long term loans with 3% interest subvention for post-harvest agri infrastructure projects.',
    benefits: [
      { id: 'b-011-1', label: '3% interest subvention for 7 years' },
      { id: 'b-011-2', label: 'Credit guarantee cover under CGTMSE' },
      { id: 'b-011-3', label: 'Loans for cold storage, warehouses, silos' },
      { id: 'b-011-4', label: 'Applicable to FPOs, SHGs, cooperatives' },
    ],
    eligibility: [
      { id: 'e-011-1', criterion: 'Farmers, FPOs, PACS, Marketing Cooperative Societies' },
      { id: 'e-011-2', criterion: 'SHGs, Joint Liability Groups' },
      { id: 'e-011-3', criterion: 'Multipurpose Cooperative Societies, Agri-entrepreneurs' },
      { id: 'e-011-4', criterion: 'Central/State government agencies' },
    ],
    documents: [
      { id: 'd-011-1', name: 'Project Report / DPR', mandatory: true },
      { id: 'd-011-2', name: 'Registration Certificate (for FPO/SHG)', mandatory: true },
      { id: 'd-011-3', name: 'Land Documents', mandatory: true },
      { id: 'd-011-4', name: 'Bank Account Details', mandatory: true },
    ],
    applicationSteps: [
      {
        id: 'as-011-1',
        stepNumber: 1,
        title: 'Register on AIF Portal',
        description: 'Register at agriinfra.dac.gov.in',
        portal: 'agriinfra.dac.gov.in',
        portalUrl: 'https://agriinfra.dac.gov.in',
      },
      {
        id: 'as-011-2',
        stepNumber: 2,
        title: 'Submit Project Proposal',
        description: 'Submit detailed project report and required documents online',
      },
      {
        id: 'as-011-3',
        stepNumber: 3,
        title: 'Bank Appraisal',
        description: 'Lending institution appraises the project and sanctions loan',
      },
    ],
    launchDate: '09/08/2020',
    deadline: '31/03/2029',
    beneficiaryCount: '70,000+ projects',
    fundingAmount: '3% interest subvention',
    states: ['All States'],
    schemeCode: 'AIF-2020',
    officialUrl: 'https://agriinfra.dac.gov.in',
    isNew: true,
    isFeatured: false,
    tags: ['agriculture', 'infrastructure', 'FPO', 'cold storage', 'loan'],
    minAge: 18,
    maxAge: undefined,
    maxIncome: undefined,
    gender: 'All',
  },
  {
    id: 'scheme-012',
    title: 'PM Ujjwala Yojana 2.0 (PMUY)',
    shortTitle: 'PM Ujjwala',
    ministry: 'Ministry of Petroleum & Natural Gas',
    category: 'Social Welfare',
    status: 'Active',
    description:
      'Pradhan Mantri Ujjwala Yojana provides LPG connections to BPL households to safeguard health of women and children by replacing the use of unclean cooking fuels. PMUY 2.0 extends coverage to migrant families without address proof requirements.',
    shortDescription:
      'Free LPG connection with first refill and hotplate for BPL households — especially migrant women.',
    benefits: [
      { id: 'b-012-1', label: 'Free LPG connection (deposit-free)' },
      { id: 'b-012-2', label: 'First refill and hotplate free' },
      { id: 'b-012-3', label: 'No address proof required for migrants' },
      { id: 'b-012-4', label: 'EMI facility for refill cylinders' },
    ],
    eligibility: [
      { id: 'e-012-1', criterion: 'Adult woman from BPL household' },
      { id: 'e-012-2', criterion: 'No existing LPG connection in the household' },
      { id: 'e-012-3', criterion: 'Listed in SECC-2011 data or any state BPL list' },
      {
        id: 'e-012-4',
        criterion: 'SC/ST households, forest dwellers, PMAY beneficiaries also eligible',
      },
    ],
    documents: [
      { id: 'd-012-1', name: 'Aadhaar Card', mandatory: true },
      { id: 'd-012-2', name: 'BPL Ration Card / SECC data', mandatory: true },
      { id: 'd-012-3', name: 'Bank Account Details', mandatory: true },
      { id: 'd-012-4', name: 'Self-declaration for address (migrants)', mandatory: false },
    ],
    applicationSteps: [
      {
        id: 'as-012-1',
        stepNumber: 1,
        title: 'Visit LPG Distributor',
        description: 'Visit nearest LPG distributor of IOCL, BPCL, or HPCL',
      },
      {
        id: 'as-012-2',
        stepNumber: 2,
        title: 'Fill KYC Form',
        description: 'Fill the PMUY application/KYC form with required details',
      },
      {
        id: 'as-012-3',
        stepNumber: 3,
        title: 'Submit Documents',
        description: 'Submit Aadhaar and BPL proof to distributor',
      },
      {
        id: 'as-012-4',
        stepNumber: 4,
        title: 'Receive Connection',
        description: 'Connection issued within 7 working days after verification',
      },
    ],
    launchDate: '10/08/2021',
    deadline: null,
    beneficiaryCount: '9.6 Crore',
    fundingAmount: 'Free connection + first refill',
    states: ['All States'],
    schemeCode: 'PMUY-2021',
    officialUrl: 'https://pmuy.gov.in',
    isNew: false,
    isFeatured: false,
    tags: ['LPG', 'BPL', 'women', 'cooking', 'social welfare'],
    minAge: 18,
    maxAge: undefined,
    maxIncome: undefined,
    gender: 'Female',
  },
];

export const CATEGORIES_META: Record<
  SchemeCategory,
  { color: string; bg: string; icon: string; description: string }
> = {
  Education: {
    color: 'text-blue-700',
    bg: 'bg-blue-50',
    icon: 'GraduationCap',
    description: 'Scholarships, fellowships, and educational grants',
  },
  Agriculture: {
    color: 'text-green-700',
    bg: 'bg-green-50',
    icon: 'Wheat',
    description: 'Farmer support, crop insurance, and agri-infrastructure',
  },
  Health: {
    color: 'text-red-700',
    bg: 'bg-red-50',
    icon: 'HeartPulse',
    description: 'Health insurance and medical assistance programs',
  },
  Business: {
    color: 'text-purple-700',
    bg: 'bg-purple-50',
    icon: 'Briefcase',
    description: 'MSME loans, startup support, and entrepreneurship',
  },
  Women: {
    color: 'text-pink-700',
    bg: 'bg-pink-50',
    icon: 'Users',
    description: 'Women empowerment, safety, and financial inclusion',
  },
  Housing: {
    color: 'text-amber-700',
    bg: 'bg-amber-50',
    icon: 'Home',
    description: 'Affordable housing, subsidies, and urban development',
  },
  'Social Welfare': {
    color: 'text-teal-700',
    bg: 'bg-teal-50',
    icon: 'HandHeart',
    description: 'BPL support, LPG, and social security schemes',
  },
  'Rural Development': {
    color: 'text-lime-700',
    bg: 'bg-lime-50',
    icon: 'TreePine',
    description: 'Rural infrastructure, employment, and development',
  },
  'Skill Development': {
    color: 'text-orange-700',
    bg: 'bg-orange-50',
    icon: 'Wrench',
    description: 'Vocational training, apprenticeships, and skill upgradation',
  },
};

export function getSchemeById(id: string): Scheme | undefined {
  return SCHEMES.find((s) => s.id === id);
}

export function getSchemesByCategory(category: SchemeCategory): Scheme[] {
  return SCHEMES.filter((s) => s.category === category);
}

export function getFeaturedSchemes(): Scheme[] {
  return SCHEMES.filter((s) => s.isFeatured);
}

export function getNewSchemes(): Scheme[] {
  return SCHEMES.filter((s) => s.isNew);
}
